/**
 * Shared read-only presentation of a routine's data sources.
 *
 * Used by the routine detail page's facts card and the routine preview panel,
 * so the two attribute and glyph a source the same way — the companion of
 * `routineChannelDisplay` for the Sources row.
 */

import { Database } from 'lucide-react';
import { AppLogo } from '@/components/AppLogo';
import { resolveAppShortName } from '@/constants/appIcons';
import type { RoutineDataSource } from '@/hooks/useRoutines';

/**
 * The provider behind a data source, for its logo — or null when we can't tell.
 *
 * Only tool sources name their provider (the pinned `tool_name` is prefixed with
 * the app short_name). A dataset or prism query reads the warehouse, and nothing
 * in the stored source identifies whose data that is: the SQL names only the
 * tenant's numbered BigQuery dataset, and resolving a cube to its provider needs
 * the prism model definitions, which don't exist client-side. Those sources get a
 * neutral warehouse glyph rather than the Strive mark — the data is the
 * customer's (Google Ads, LinkedIn, …), so badging it as ours states something
 * false. Real attribution has to come from the server; until it does, say
 * nothing rather than the wrong thing.
 *
 * @param source - A routine data source as stored on the definition.
 *
 * @returns The app short_name, or null when the provider is unresolvable.
 */
export function routineSourceApp(source: RoutineDataSource): string | null {
  if (source.type === 'tool' && source.tool_name) {
    return resolveAppShortName(source.tool_name);
  }
  return null;
}

/**
 * A source's 16px mark: the connector logo, or the neutral warehouse glyph when
 * the provider is unresolved (see {@link routineSourceApp}). Both branches are
 * the same size so rows don't jump between resolved and unresolved sources, and
 * the mark sits inline with the label rather than towering over it.
 */
export function RoutineSourceGlyph({
  appShortName,
  displayName,
}: {
  appShortName: string | null;
  displayName: string;
}) {
  return (
    <span className="flex h-4 w-4 shrink-0 items-center justify-center">
      {appShortName ? (
        <AppLogo shortName={appShortName} displayName={displayName} size="xs" />
      ) : (
        <Database className="h-4 w-4 text-muted-foreground" aria-hidden />
      )}
    </span>
  );
}
